import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {DISPLAY_MESSAGE_IDS, getDisplayMessageName} from "../../../common/const/casinofied-analytics-consts";
import {IGamblingSessionAnalytics} from "./gambling-session-analytics.interface";
import {IGamblingSessionLimitsSetup} from "./gambling-session-limits-setup.interface";
import {IAnalyticsMessageExtraData} from "../../../common/interfaces/casinofied-analytics-proxy.interface";
import {
    GamblingSessionActionEventData
} from "../firebase/topics/messages/publish-data/gambling-session-action.event-data";

export class GamblingSessionAnalytics implements IGamblingSessionAnalytics {
    constructor(private readonly services: IServiceFactoryExtended) {

    }

    private get userBankroll(): number {
        return this.services.wallet.totalBankroll;
    }


    private get userBankrollCurrency(): string {
        return this.services.currency.currencyIso3;
    }

    reportSetLimitPopupShown(correlationId: string): void {
        this.services.casinofiedAnalytics.messageDisplay({
            displayMessageID: DISPLAY_MESSAGE_IDS.SDSetLimitPopUp,
            displayMessageName: "SDSetLimitPopUp",
            businessCorrelationID: correlationId,
            correlationID: correlationId
        })
    }

    reportConfirmSetLimitPopup(correlationId: string, limits: IGamblingSessionLimitsSetup, errorCode: number, gamblingSessionId: string): void {
        this.services.casinofiedAnalytics.messageActionTaken({
            displayMessageName: getDisplayMessageName(DISPLAY_MESSAGE_IDS.SDSetLimitPopUp),
            actionType: "Set",
            businessCorrelationID: correlationId,
            correlationID: correlationId,
            extraData: {
                sessionRequestedNetLossAmountLimit: limits.lossLimitAmount,
                sessionRequestedTimeLimitInSeconds: limits.sessionDurationLimit.totalSeconds,
                sessionRequestedTimeReminderInSeconds: limits.reminderInterval.totalSeconds,
                sessionRequestedRestrictionInSeconds: limits.restrictionPeriod.totalSeconds,
                gamblingSessionId: gamblingSessionId,
                errorCode: errorCode
            }
        });
    }

    reportCancelSetLimitPopup(correlationId: string): void {
        this.services.casinofiedAnalytics.messageActionTaken({
            displayMessageName: getDisplayMessageName(DISPLAY_MESSAGE_IDS.SDSetLimitPopUp),
            actionType: "Cancel",
            businessCorrelationID: correlationId,
            correlationID: correlationId
        });
    }

    private _reportPopupShown(messageId: number, data: GamblingSessionActionEventData) {
        this.services.casinofiedAnalytics.messageDisplay({
            displayMessageID: messageId,
            displayMessageName: getDisplayMessageName(messageId),
            businessCorrelationID: data.correlationID,
            correlationID: data.correlationID,
            extraData: {
                bankrollCurr: this.userBankrollCurrency,
                amountBankroll: this.userBankroll,
                messageTypeSource: data.messageType,
                sessionDurationInSeconds: data.sessionDurationInSeconds,
                totalStakes: data.sessionTotalBets,
                totalWins: data.sessionTotalWins,
                gamblingSessionId: data.gamblingSessionId
            }
        });
    }

    reportNetLossAdvancedWarningShown(data: GamblingSessionActionEventData): void {
        this._reportPopupShown(
            DISPLAY_MESSAGE_IDS.SDNetLossAdvanceWarning,
            data
        )
    }

    reportTimeLimitAdvancedWarningShown(data: GamblingSessionActionEventData): void {
        this._reportPopupShown(
            DISPLAY_MESSAGE_IDS.SDTimeLimitAdvanceWarning,
            data
        )
    }

    reportNetLossLimitReachedShown(data: GamblingSessionActionEventData): void {
        this._reportPopupShown(
            DISPLAY_MESSAGE_IDS.SDNetLossLimitsReached,
            data
        )
    }

    reportTimeLimitReachedShown(data: GamblingSessionActionEventData): void {
        this._reportPopupShown(
            DISPLAY_MESSAGE_IDS.SDTimeLimitsReached,
            data
        )
    }

    reportRealityCheckShow(data: GamblingSessionActionEventData): void {
        this._reportPopupShown(
            DISPLAY_MESSAGE_IDS.SDRealityCheck ,
            data
        )
    }

    reportMessageConfirmed(messageId: number, data: GamblingSessionActionEventData) {
        this.services.casinofiedAnalytics.messageActionTaken({
            displayMessageName: getDisplayMessageName(messageId),
            actionType: 'Confirm',
            businessCorrelationID: data.correlationID,
            correlationID: data.correlationID,
            extraData: {
                messageTypeSource: data.messageType,
                gamblingSessionId: data.gamblingSessionId
            }
        });
    }

    private async _getAnalyticsExtraData(): Promise<Partial<IAnalyticsMessageExtraData>> {
        const sessionData = await this.services.gamblingSession.getGamblingSessionState();
        return {
            amountBankroll: this.userBankroll,
            bankrollCurr: this.userBankrollCurrency,
            totalStakes: sessionData.sessionTotalBets,
            totalWins: sessionData.sessionTotalWins,
            netPosition: sessionData.sessionTotalBets - sessionData.sessionTotalWins
        }
    }

    async reportLogoutMessageShown(): Promise<void> {
        const correlationId = this.services.tools.getGuid();
        const extraData = await this._getAnalyticsExtraData();
        this.services.casinofiedAnalytics.messageDisplay({
            displayMessageID: DISPLAY_MESSAGE_IDS.SDResult,
            displayMessageName: getDisplayMessageName(DISPLAY_MESSAGE_IDS.SDResult),
            businessCorrelationID: correlationId,
            correlationID: correlationId,
            extraData: extraData
        })
    }

    async reportLogoutMessageActionTaken(actionType: 'Cancel' | 'Logout'): Promise<void> {
        const correlationId = this.services.tools.getGuid();
        const extraData = await this._getAnalyticsExtraData();
        this.services.casinofiedAnalytics.messageActionTaken({
           displayMessageName: getDisplayMessageName(DISPLAY_MESSAGE_IDS.SDResult),
            actionType: actionType,
            businessCorrelationID: correlationId,
            correlationID: correlationId,
            extraData: extraData
        });
    }

}